import {CommonModule, Location} from '@angular/common';
import {Component, computed, inject, signal, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router} from '@angular/router';
import {MatMenuModule} from '@angular/material/menu';
import {ButtonType, CovButtonComponent, CovIconComponent, IconName} from '@cov/ui-angular';
import {TranslocoModule} from '@jsverse/transloco';
import {filter, Observable} from 'rxjs';
import {SearchBarComponent} from '../../../features/search/ui/search-bar/search-bar.component';
import {AsmData} from '../../../interfaces/asm-data.interface';
import {UserData} from '../../../interfaces/user-data.interface';
import {DOCUMENT_TITLE} from '../../constants';
import {RevealFixedTopBarDirective} from '../../directives/reveal-fixed-top-bar.directive';
import {AsmService} from '../../services/asm.service';
import {EnvironmentService} from '../../services/environment.service';
import {PageService} from '../../services/page.service';
import {SoldToShipToService} from '../../services/sold-to-ship-to.service';
import {UserService} from '../../services/user.service';
import {mapToViewAddress} from '../../utils/address.utils';
import {CartDisplayComponent} from '../cart-display/cart-display.component';
import {SoldToInformationMenuComponent} from '../sold-to-information-menu/sold-to-information-menu.component';
import {SoldToInformationComponent} from '../sold-to-information/sold-to-information.component';
import {UserPreferencesComponent} from '../user-preferences/user-preferences.component';
import {WalkmeInfoComponent} from '../walkme-info/walkme-info.component';

@Component({
  selector: 'cov-top-bar',
  imports: [
    CommonModule,
    RevealFixedTopBarDirective,
    CovIconComponent,
    UserPreferencesComponent,
    MatMenuModule,
    CovButtonComponent,
    TranslocoModule,
    SoldToInformationComponent,
    SoldToInformationMenuComponent,
    CartDisplayComponent,
    SearchBarComponent,
    WalkmeInfoComponent,
  ],
  templateUrl: './top-bar.component.html',
  styleUrl: './top-bar.component.scss',
})
export class TopBarComponent implements OnInit {
  // CONST
  protected readonly ButtonType = ButtonType;
  protected buttonType = ButtonType;
  protected iconName = IconName;

  // SERVICE
  protected pageService = inject(PageService);
  protected location = inject(Location);
  protected router = inject(Router);
  protected asmService = inject(AsmService);
  protected userService = inject(UserService);
  protected soldToService = inject(SoldToShipToService);
  public environmentService = inject(EnvironmentService);

  // STATE
  protected impersonateActiveSession$: Observable<boolean> =
    this.asmService.getImpersonateActiveSession();
  public pageTitle = DOCUMENT_TITLE;
  public pageTitle$ = this.pageService.getCurrentPageTitle();
  public showBackLink$ = this.pageService.getShowBackLink();
  public isSoldToInformationOpen = signal(false);
  public currentUser$!: Observable<UserData | null>;
  public asmAgent$!: Observable<AsmData | null>;
  public walkMe = this.environmentService.isWalkMeEnabled;
  public shouldHideSearchBar = signal(false);
  public shownSoldTo = computed(() => {
    const addresses = this.soldToService.allAddresses();
    if (addresses?.length === 1) {
      return mapToViewAddress(addresses[0]);
    }
    return null;
  });

  constructor() {
    // Check initial route
    this.updateSearchBarVisibility(this.router.url);

    // Listen for route changes
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event: NavigationEnd) => {
        this.updateSearchBarVisibility(event.urlAfterRedirects);
      });
  }

  ngOnInit(): void {
    this.currentUser$ = this.userService.getCurrentUser();
    this.asmAgent$ = this.asmService.getAsmAgent();
  }

  private updateSearchBarVisibility(url: string): void {
    // Hide search bar on order templates page (has its own search)
    this.shouldHideSearchBar.set(url.includes('/orders/templates'));
  }

  // METHODS
  public back() {
    this.location.back();
  }
}
